import type { PractitionerRole } from '@medplum/fhirtypes'
import { SNOMED_USER_ROLES } from '@/utils'

/**
 * True when ANY of the practitioner's PractitionerRole rows carries a manager
 * coding — care coordinator ("Hospital manager", 303129000) or call-center
 * manager (224565004).
 *
 * Mirrors the middleware source of truth for manager detection —
 * packages/middleware/src/model/practitioner/internal/role-codes.ts
 * (`roleHasAnyCode` + `MANAGER_ROLE_CODES` + `PRACTITIONER_ROLE_SYSTEM`). The BE
 * is authoritative for "is this practitioner a manager"; this helper matches its
 * rule so FE gating agrees: inspect EVERY role and EVERY coding, require the
 * practitioner-role `system`, and treat any single manager coding as manager.
 * (The legacy FE read only `code[0].coding[0]` with no system check, which is
 * non-deterministic for a practitioner holding multiple PractitionerRole rows.)
 */
// All SNOMED_USER_ROLES entries share this system; mirrors PRACTITIONER_ROLE_SYSTEM in the BE.
const MANAGER_ROLE_SYSTEM = SNOMED_USER_ROLES.HOSPITAL_MANAGER.url
const MANAGER_ROLE_CODES = new Set([
  SNOMED_USER_ROLES.HOSPITAL_MANAGER.code, // 303129000
  SNOMED_USER_ROLES.CC_MANAGER.code, // 224565004
])
const CALL_CENTER_MANAGER_CODES = new Set([SNOMED_USER_ROLES.CC_MANAGER.code])

function roleHasAnyCode(roles: PractitionerRole[] | undefined | null, codes: Set<string>): boolean {
  return (roles ?? []).some((role) =>
    (role.code ?? []).some((concept) =>
      (concept.coding ?? []).some(
        (coding) =>
          coding.system === MANAGER_ROLE_SYSTEM && !!coding.code && codes.has(coding.code),
      ),
    ),
  )
}

export function isManagerRole(roles: PractitionerRole[] | undefined | null): boolean {
  return roleHasAnyCode(roles, MANAGER_ROLE_CODES)
}

/**
 * Call-center managers (224565004) only — excludes care coordinators. The Default
 * Pharmacy card gates on this narrower check: the card is a demographics surface and
 * care coordinators can edit demographics, so only call-center managers are blocked
 * there (PD-1386).
 */
export function isCallCenterManagerRole(roles: PractitionerRole[] | undefined | null): boolean {
  return roleHasAnyCode(roles, CALL_CENTER_MANAGER_CODES)
}
